import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { ArrowLeft, Check, ImagePlus, KeyRound, Loader2, LogOut, Package, Phone, Plus, Receipt, Trash2 } from 'lucide-react'
import { Link } from 'react-router'

import { categories, type Category, type Product } from '../../data/products'
import { LanguageButtons } from '../components/Nav'
import { Button, inputClass } from '../components/ui'
import { money, useCatalog } from '../lib/catalog'
import { cx } from '../lib/cx'
import { t, useI18n } from '../lib/i18n'

type Tab = 'contact' | 'products' | 'orders'

interface Settings {
  shopUsername: string | null
  instagram: string | null
  whatsapp: string | null
  phone: string | null
  email: string | null
}

interface Order {
  id: number
  product_name: string
  quantity: number
  price: number | null
  total: number | null
  customer: string
  created_at: string
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { headers: { 'content-type': 'application/json' }, ...init })
  const data = (await response.json().catch(() => ({}))) as T & { error?: string }
  if (!response.ok) throw new Error(data.error || t('request_failed'))
  return data
}

export function Admin() {
  useI18n()
  const [status, setStatus] = useState<'checking' | 'out' | 'in'>('checking')

  useEffect(() => {
    api<{ authenticated: boolean }>('/api/admin/me')
      .then((data) => {
        setStatus(data.authenticated ? 'in' : 'out')
      }
      ).catch(() => setStatus('out'))
  }, [])

  if (status === 'checking') {
    return (
      <div className="grid min-h-screen place-items-center text-muted">
        <Loader2 className="size-5 animate-spin" />
      </div>
    )
  }

  if (status === 'out') return <Login onDone={() => setStatus('in')} />

  return (
    <Panel
      onLogout={async () => {
        await fetch('/api/admin/logout', { method: 'POST' }).catch(() => undefined)
        setStatus('out')
      }}
    />
  )
}

function Login({ onDone }: { onDone: () => void }) {
  useI18n()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/api/admin/login', { method: 'POST', body: JSON.stringify({ password }) })
      onDone()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative grid min-h-screen place-items-center px-5 py-20">
      <LanguageButtons className="absolute right-3 top-3 sm:right-6 sm:top-4" />
      <div className="w-full max-w-[400px]">
        <Link to="/" className="inline-flex min-h-11 items-center gap-1.5 text-[13.5px] text-muted transition-colors hover:text-forest">
          <ArrowLeft className="size-4" /> {t('back_to_shop')}
        </Link>
        <div className="mt-6 rounded-[24px] border border-line bg-surface p-7 shadow-soft">
          <span className="grid size-11 place-items-center rounded-[14px] bg-forest/10 text-forest">
            <KeyRound className="size-5" />
          </span>
          <h1 className="font-display mt-4 text-[34px] leading-none">{t("admin")}</h1>
          <p className="mt-2 text-[14px] text-muted">{t('admin_description')}</p>
          <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
            <label className="text-[13px] font-medium" htmlFor="password">{t("password")}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder="••••••••"
            />
            {error && <p className="text-[13px] text-accent">{error}</p>}
            <Button type="submit" loading={busy} className="mt-1 w-full">{t("login")}</Button>
          </form>
        </div>
      </div>
    </div>
  )
}

function Panel({ onLogout }: { onLogout: () => void }) {
  useI18n()
  const [tab, setTab] = useState<Tab>('contact')
  const tabs: { key: Tab; label: string; icon: typeof Phone }[] = [
    { key: 'contact', label: t('contact_settings'), icon: Phone },
    { key: 'products', label: t('products_tab'), icon: Package },
    { key: 'orders', label: t('orders_tab'), icon: Receipt },
  ]

  return (
    <div className="min-h-screen bg-bg">
      <header className="sticky top-0 z-30 border-b border-line bg-bg/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-4 px-5">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex min-h-11 items-center text-[13.5px] text-muted transition-colors hover:text-fg">← {t('shop')}</Link>
            <span className="h-4 w-px bg-line-strong" />
            <p className="font-display text-[22px] leading-none">{t("admin")}</p>
          </div>
          <button type="button" onClick={onLogout} className="inline-flex min-h-11 items-center gap-1.5 rounded-[11px] px-3 text-[13.5px] text-muted transition-colors hover:bg-surface-2 hover:text-fg">
            <LogOut className="size-4" /> {t("logout")}
          </button>
        </div>
        <div className="mx-auto max-w-[1120px] px-3">
          <LanguageButtons className="justify-end" />
        </div>
        <div className="mx-auto flex max-w-[1120px] gap-1 overflow-x-auto px-3 pb-2.5">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cx(
                'inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-[11px] px-3 text-[13.5px] transition-colors',
                tab === key ? 'bg-fg text-bg' : 'text-muted hover:bg-surface-2 hover:text-fg',
              )}
            >
              <Icon className="size-4" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-5 py-8 pb-24">
        {tab === 'contact' && <ContactTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'orders' && <OrdersTab />}
      </main>
    </div>
  )
}

function Card({ title, description, children, className }: { title: string; description?: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-[22px] border border-line bg-surface p-6', className)}>
      <h2 className="text-[16px] font-medium">{title}</h2>
      {description && <p className="mt-1 text-[13.5px] leading-relaxed text-muted">{description}</p>}
      <div className="mt-5">{children}</div>
    </section>
  )
}

function ContactTab() {
  useI18n()
  const { refresh } = useCatalog()
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [shopUsername, setShopUsername] = useState('')
  const [instagram, setInstagram] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const data = await api<Settings>('/api/admin/settings')
    setPhone(data.phone ?? '')
    setEmail(data.email ?? '')
    setShopUsername(data.shopUsername ?? '')
    setInstagram(data.instagram ?? '')
    setWhatsapp(data.whatsapp ?? '')
  }, [])

  useEffect(() => {
    load().catch((err) => setError((err as Error).message))
  }, [load])

  async function save(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const body = { phone, email, shopUsername, instagram, whatsapp }
      const data = await api<Settings>('/api/admin/settings', { method: 'PUT', body: JSON.stringify(body) })
      setPhone(data.phone ?? '')
      setEmail(data.email ?? '')
      setShopUsername(data.shopUsername ?? '')
      setInstagram(data.instagram ?? '')
      setWhatsapp(data.whatsapp ?? '')
      refresh()
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2400)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl">
      <Card title={t('contact_settings')} description={t('contact_settings_help')}>
        <form onSubmit={save} className="flex flex-col gap-5">
          <Field label={t('phone')} hint={t('phone_help')}>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998 90 123 45 67" className={inputClass} autoComplete="tel" inputMode="tel" />
          </Field>

          <Field label={t('email')} hint={t('email_help')}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@example.com" className={inputClass} autoComplete="email" />
          </Field>

          <Field label={t('shop_username')} hint={t('shop_username_help')}>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[15px] text-faint">@</span>
              <input
                value={shopUsername.replace(/^@/, '')}
                onChange={(e) => setShopUsername(e.target.value)}
                placeholder="your_shop"
                className={cx(inputClass, 'pl-8')}
                autoComplete="off"
              />
            </div>
          </Field>

          <Field label={t("instagram")} hint={t('instagram_help')}>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[15px] text-faint">@</span>
              <input
                value={instagram.replace(/^@/, '')}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="oltin.hosil"
                className={cx(inputClass, 'pl-8')}
                autoComplete="off"
              />
            </div>
          </Field>

          <Field label={t('whatsapp')} hint={t('whatsapp_help')}>
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="+998 90 123 45 67"
              className={inputClass}
              autoComplete="tel"
              inputMode="tel"
            />
          </Field>

          {error && <p className="rounded-[12px] bg-accent-soft px-3 py-2 text-[13px] text-accent">{error}</p>}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" loading={busy}>{t("save")}</Button>
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-[13px] text-forest">
                <Check className="size-4" /> {t("saved")}
              </span>
            )}
          </div>
        </form>
      </Card>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium">{label}</span>
      {children}
      {hint && <span className="text-[12px] text-faint">{hint}</span>}
    </label>
  )
}

// Products ------------------------------------------------------------------

const emptyForm = {
  name: '',
  slug: '',
  category: 'mayiz' as Category,
  image: '',
  price: '',
  short: '',
  description: '',
  details: '',
  materials: '',
  badge: '',
  featured: false,
}

type ProductForm = typeof emptyForm

async function compressImage(file: File): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error('image_invalid')
  }
  try {
    let scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height))
    for (let attempt = 0; attempt < 10; attempt++) {
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(bitmap.width * scale))
      canvas.height = Math.max(1, Math.round(bitmap.height * scale))
      const context = canvas.getContext('2d')
      if (!context) throw new Error('image_compress_failed')
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', Math.max(0.55, 0.82 - attempt * 0.04)))
      if (!blob) throw new Error('image_compress_failed')
      if (blob.size <= 2 * 1024 * 1024) return blob
      scale *= 0.82
    }
    throw new Error('image_size')
  } finally {
    bitmap.close()
  }
}

function imageDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('image_upload_failed'))
    }
    reader.onerror = () => reject(new Error('image_upload_failed'))
    reader.readAsDataURL(blob)
  })
}

const toForm = (product: Product): ProductForm => ({
  name: product.name,
  slug: product.slug,
  category: product.category,
  image: product.image,
  price: product.price === undefined ? '' : String(product.price),
  short: product.short,
  description: product.description,
  details: product.details.join('\n'),
  materials: product.materials,
  badge: product.badge ?? '',
  featured: Boolean(product.featured),
})

function ProductsTab() {
  useI18n()
  const [products, setProducts] = useState<Product[]>([])
  const [form, setForm] = useState<ProductForm | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  const [imageBusy, setImageBusy] = useState(false)

  const load = useCallback(async () => {
    const data = await api<{ products: Product[] }>('/api/admin/products')
    setProducts(data.products)
  }, [])

  useEffect(() => {
    load()
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [load])

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!form) return
    if (!form.image) {
      setImageError(t('image_missing'))
      return
    }
    setBusy(true)
    setError(null)
    setImageError(null)
    const body = {
      ...form,
      slug: form.slug || undefined,
      price: form.price === '' ? null : Number(form.price),
      details: form.details.split('\n').map((line) => line.trim()).filter(Boolean),
      badge: form.badge || null,
    }
    try {
      if (products.some((p) => p.slug === form.slug && form.slug)) {
        await api(`/api/admin/products/${form.slug}`, { method: 'PUT', body: JSON.stringify(body) })
      } else {
        await api('/api/admin/products', { method: 'POST', body: JSON.stringify(body) })
      }
      setForm(null)
      setPreviewUrl(null)
      await load()
    } catch {
      setError(t('request_failed'))
    } finally {
      setBusy(false)
    }
  }

  async function selectImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !form || imageBusy) return
    setImageError(null)
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setImageError(t('image_type'))
      return
    }

    setImageBusy(true)
    try {
      const compressed = await compressImage(file)
      setPreviewUrl(await imageDataUrl(compressed))
      const upload = new FormData()
      upload.append('file', compressed, 'product.webp')
      const response = await fetch('/api/admin/upload', { method: 'POST', body: upload })
      const data = (await response.json().catch(() => ({}))) as { image?: string; error?: string }
      const imageUrl = typeof data.image === 'string' && data.image.trim() ? data.image : null
      if (!response.ok || !imageUrl) {
        const key = data.error === 'image_type' ? 'image_type' : data.error === 'image_size' ? 'image_size' : data.error === 'image_missing' ? 'image_missing' : 'image_upload_failed'
        throw new Error(key)
      }
      setForm((current) => current ? { ...current, image: imageUrl } : current)
      setPreviewUrl(imageUrl)
    } catch (error) {
      const key = error instanceof Error && ['image_invalid', 'image_compress_failed', 'image_size', 'image_type'].includes(error.message)
        ? error.message
        : 'image_upload_failed'
      setImageError(t(key))
      setPreviewUrl(form.image || null)
    } finally {
      setImageBusy(false)
    }
  }

  async function remove(slug: string) {
    if (!window.confirm(t('delete_confirm'))) return
    setError(null)
    try {
      await api(`/api/admin/products/${slug}`, { method: 'DELETE' })
      await load()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const editing = Boolean(form && products.some((p) => p.slug === form.slug))

  return (
    <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
      <Card title={t('product_count_title', { count: products.length })} description={t('price_hint')}>
        {error && <p className="mb-3 rounded-[12px] bg-accent-soft px-3 py-2 text-[13px] text-accent">{error}</p>}
        {loading ? (
          <div className="grid h-32 place-items-center text-muted">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {products.map((product) => (
              <li key={product.slug} className="flex items-center gap-3 py-3">
                <span className="image-frame relative grid size-12 shrink-0 place-items-center overflow-hidden rounded-[10px]">
                  <Package className="size-5 text-faint" />
                  {product.image && (
                    <img
                      key={product.image}
                      src={product.image}
                      alt=""
                      loading="lazy"
                      onError={(event) => { event.currentTarget.hidden = true }}
                      className="absolute inset-0 size-full object-cover"
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-medium">{product.name}</p>
                  {product.badge && (
                    <span className="ml-2 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted">
                      {t(product.badge === 'New' ? 'badge_new_label' : product.badge === 'Bestseller' ? 'badge_bestseller_label' : 'badge_low_stock_label')}
                    </span>
                  )}
                  <p className="truncate text-[12.5px] text-muted">
                    {product.price !== undefined ? money(product.price) : t('no_price_admin')} · {t(`category_${product.category}`)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setForm(toForm(product))
                    setError(null)
                    setPreviewUrl(null)
                    setImageError(null)
                  }}
                  className="inline-flex min-h-11 items-center rounded-[9px] px-2.5 text-[12.5px] text-muted transition-colors hover:bg-surface-2 hover:text-fg"
                >{t('edit')}</button>
                <button
                  type="button"
                  aria-label={t('delete')}
                  onClick={() => remove(product.slug)}
                  className="grid size-11 place-items-center rounded-[9px] text-muted transition-colors hover:bg.accent-soft hover:text-accent"
                >
                  <Trash2 className="size-4" /> {t('delete')}
                </button>
              </li>
            ))}
            {products.length === 0 && <li className="py-6 text-center text-[13.5px] text-muted">{t('no_products')}</li>}
          </ul>
        )}
        <Button variant="secondary" className="mt-4" onClick={() => {
          setForm({ ...emptyForm })
          setPreviewUrl(null)
          setImageError(null)
        }}><Plus /> {t('new_product')}</Button>
      </Card>

      {form && (
        <Card title={editing ? t('editing_product') : t('new_product')}>
          <form onSubmit={save} className="flex flex-col gap-4">
            <Field label={t('name')}>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputClass} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('category')}>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Category })} className={inputClass}>
                  {categories.map((c) => (
                    <option key={c.key} value={c.key}>
                      {t(`category_${c.key}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('price')}>
                <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} inputMode="decimal" placeholder="78000" className={inputClass} />
              </Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">{t('image_url')}</span>
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <div className="image-frame relative grid aspect-square w-32 shrink-0 place-items-center overflow-hidden rounded-[14px] border border-line bg-surface-2">
                  <span className="px-2 text-center text-[12px] text-faint">{t('image_none')}</span>
                  {(previewUrl ?? form.image) && (
                    <img
                      key={previewUrl ?? form.image}
                      src={previewUrl ?? form.image}
                      alt={t('image_preview')}
                      onError={(event) => { event.currentTarget.hidden = true }}
                      className="absolute inset-0 size-full object-cover"
                    />
                  )}
                </div>
                <div className="flex flex-col items-start gap-2">
                  <input id="product-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={selectImage} disabled={imageBusy} className="sr-only" />
                  <label htmlFor="product-image" aria-disabled={imageBusy} className={cx('inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-line-strong px-4 text-[14px] font-medium text-fg transition-colors hover:border-forest hover:bg-forest-soft', imageBusy && 'pointer-events-none opacity-60')}>
                    {imageBusy ? <Loader2 key="busy" className="size-4 animate-spin" /> : <ImagePlus key="idle" className="size-4" />}
                    <span>{imageBusy ? t('image_uploading') : form.image ? t('image_change') : t('image_choose')}</span>
                  </label>
                  {imageError && <p className="text-[12.5px] text-accent">{imageError}</p>}
                </div>
              </div>
              <span className="text-[12px] text-faint">{t('image_hint')}</span>
            </div>
            <Field label={t('short_description')}>
              <input value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} className={inputClass} />
            </Field>
            <Field label={t('full_description')}>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="w-full rounded-control border border-line-strong bg-surface px-4 py-3 text-[15px] focus:border-fg focus:outline-none"
              />
            </Field>
            <Field label={t('product_details')} hint={t('details_hint')}>
              <textarea
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                rows={3}
                className="w-full rounded-control border border-line-strong bg-surface px-4 py-3 text-[15px] focus:border-fg focus:outline-none"
              />
            </Field>
            <Field label={t('materials_storage')}>
              <input value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} className={inputClass} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('badge')}>
                <select value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputClass}>
                  <option value="">{t('no_badge')}</option>
                  <option value="New">{t('badge_new')}</option>
                  <option value="Bestseller">{t('badge_bestseller')}</option>
                  <option value="Low stock">{t('badge_low_stock')}</option>
                </select>
              </Field>
              <label className="flex items-end gap-2 pb-3 text-[13.5px]">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="size-4 accent-[#254b35]" /> {t('show_on_home')}
              </label>
            </div>
            {editing && (
              <Field label={t('slug')} hint={t('slug_hint')}>
                <input value={form.slug} readOnly className={cx(inputClass, 'bg-surface-2 text-muted')} />
              </Field>
            )}
            <div className="flex gap-2">
              <Button type="submit" loading={busy} disabled={imageBusy || !form.image}>{t("save")}</Button>
              <Button type="button" variant="secondary" disabled={imageBusy || busy} onClick={() => {
                setForm(null)
                setPreviewUrl(null)
                setImageError(null)
              }}>{t('cancel')}</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}

function OrdersTab() {
  useI18n()
  const [orders, setOrders] = useState<Order[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api<{ orders: Order[] }>('/api/admin/orders')
      .then((data) => setOrders(data.orders))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Card
      title={t('orders_tab')}
      description={t('confirmed_orders')}
    >
      {error && (
        <p className="mb-3 rounded-[12px] bg-accent-soft px-3 py-2 text-[13px] text-accent">{error}</p>
      )}
      {loading ? (
        <div className="grid h-32 place-items-center text-muted">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <p className="py-8 text-center text-[13.5px] text-muted">{t('no_orders')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {orders.map((order) => (
            <li key={order.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium">
                  {order.product_name} <span className="text-muted">× {order.quantity}</span>
                </p>
                <p className="truncate text-[12.5px] text-muted">{order.customer}</p>
              </div>
              <p className="text-[13.5px] tabular-nums">
                {order.total !== null ? money(order.total) : 'kelishiladi'}
              </p>
              <p className="w-full text-[12px] text-faint sm:w-auto">
                {new Date(order.created_at).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}